import { Component, inject, OnInit, VERSION } from '@angular/core';
import { Router } from '@angular/router';

import { forkJoin, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Ability, AbilityBuilder } from '@casl/ability';
import { IError, IOCMessage, IUser } from '@orgacard/login';

import { environment } from '../../environments/environment';
import { AppService } from '../app.service';
import { CoreService } from '../core/core.service';
import { EnvironmentService } from '../core/environment.service';
import { PrivilegeService } from '../core/privilege.service';
import { UtilityHelperService } from '../core/utility-helper.service';
import { LoadingOverlayService } from '../loading-overlay/loading-overlay.service';
import { GenderService } from '../mwsp/basedata/general/genders/gender.service';
import { IUserBasedata } from '../mwsp/basedata/users/users/user.model';
import { UserService } from '../mwsp/basedata/users/users/user.service';
import { CostcentertreeService } from '../mwsp/shared/costcentertree/costcentertree.service';
import { SupplierService } from '../mwsp/supplier/shared/services/supplier.service';
import { AppAbilities, AppAbility, appActions } from './appAbility';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    standalone: false,
})
export class LoginComponent implements OnInit {
    private readonly utilityHelperService = inject(UtilityHelperService);
    private appService = inject(AppService);
    private router = inject(Router);
    private ability = inject<Ability<AppAbilities>>(Ability);
    private coreService = inject(CoreService);
    private environmentService = inject(EnvironmentService);
    private costcentertreeService = inject(CostcentertreeService);
    private loadingOverlayService = inject(LoadingOverlayService);
    private privilegeService = inject(PrivilegeService);
    private supplierService = inject(SupplierService);
    private genderService = inject(GenderService);
    private userService = inject(UserService);

    public version: string = environment.version;
    public env = environment;
    public ngVersion = VERSION.full;

    public get currentAppSettings() {
        // The getter takes care the app settings is up to date (every angular update cycle its getting new requested)
        return {
            ...this.environmentService.appSettings,
            lang: this.environmentService.lang,
        };
    }

    public constructor() {
        this.appService.pageTitle = this.env.applicationName === 'OCMWSI' ? 'MWS Internetplattform' : 'MWS Portal';
    }

    public ngOnInit() {
        this.loadMsgtexts();
        // if (window.location.port !== '4200') {
        this.coreService.getVersionData().subscribe((data: any) => {
            if (this.environmentService.appSettings.showLog) console.log(new Date() + ': ' + 'login.component.ts - getVersionData');
        });
        // }
    }

    public setAuthToken(authToken: string) {
        if (this.environmentService.appSettings.showLog) console.log('---> ' + JSON.stringify(authToken));
        this.environmentService.systemUser = {} as IUser;
        this.environmentService.systemUser.token = authToken;
    }

    public doLogin(loginResult: { user: IUser }) {
        if (this.environmentService.appSettings.showLog) console.log('---> ' + JSON.stringify(loginResult));

        // INIT
        this.init();
        // Issue #268: Die CASL Bibliothek interpretiert Wildcards folgendermassen:
        // '*'  : Berechtigungen auf der Ebene an der sich das Wildcard befindet. Tieferliegende Berechtigungen sind nicht eingeschlossen.
        // '**' : Berechtigungen auf der angegebenen Ebene und allen darunter liegenden Ebenen.
        //
        //  Die BackEnd-Seitigen Privilegien sehen nur * vor. Hier werden auch die darunter liegenden Ebenen miteingeschlossen.
        //  =>   * (Asterisk) durch ** ersetzen.
        loginResult.user.privileges = loginResult.user.privileges.map((priv) => priv.replace(/\*/g, '**'));
        //
        this.environmentService.systemUser = loginResult.user;

        this.environmentService.setL10NLocale(this.environmentService.getLocale());

        if (this.environmentService.appSettings.fakeServer) {
            this.privilegeService.getSystemPrivilegesByFake().subscribe(
                (data: boolean) => {
                    this.initLogin(loginResult.user);
                },
                (err: IError) => {
                    // TODO
                    // this.loadingOverlayService.stopLoading();
                    if (this.environmentService.appSettings.showLog)
                        console.log(new Date() + ': ' + 'privilege-operations.component.ts - getPrivilegeOperations ERROR: ' + err.error);
                    this.appService.showToast(
                        'error',
                        this.coreService.getMsgkey('byod.note', undefined) + ': ' + err.status,
                        this.coreService.getMsgkey(err.error.msgkey, err.error.msgargs),
                    );
                    this.logout();
                },
            );
        } else {
            this.initLogin(loginResult.user);
        }
    }

    public initLogin(loginResult: IUser) {
        localStorage.setItem('loggedInUser', JSON.stringify(this.environmentService.systemUser));
        // TODO
        // TODO
        // TODO
        this.defineAbility();

        if (loginResult.application === 'MWS-SETUP') {
            this.router.navigate(['/systemconfiguration']);
        } else {
            const observableBatch = [];
            observableBatch.push(this.coreService.getLanguageVariants().pipe(catchError((err) => throwError(err))));
            observableBatch.push(this.coreService.getJsonSchema().pipe(catchError((err) => throwError(err))));
            observableBatch.push(this.coreService.getMandator(this.environmentService.systemUser.mandatorId).pipe(catchError((err) => throwError(err))));
            observableBatch.push(this.userService.getUser());
            observableBatch.push(this.coreService.getCountries());

            if (this.environmentService.appSettings.applicationName === 'OCMWSP') {
                observableBatch.push(this.coreService.getUserCostcenters(undefined));
            }
            forkJoin(observableBatch).subscribe(
                (results: any) => {
                    if (
                        this.environmentService.userCostcenter &&
                        this.environmentService.userCostcenter.usercostcenters &&
                        this.environmentService.userCostcenter.usercostcenters.length > 0
                    ) {
                        this.environmentService.currentUserCostcenterId = this.environmentService.userCostcenter.usercostcenters[0].id;
                        this.costcentertreeService.getCostcenterTreeByTreeNode(this.environmentService.currentUserCostcenterId).subscribe(
                            (costcenters) => {
                                const currentUserCostcenter = this.costcentertreeService.findCostcenterById(
                                    costcenters,
                                    this.environmentService.currentUserCostcenterId,
                                );
                                currentUserCostcenter.selected = true;
                                // add className for productionUnits !
                                this.costcentertreeService.addSpecialAttributesToICostcenterObject(costcenters);
                                this.environmentService.userCostcenterTree = costcenters;
                                this.environmentService.userCostcenterTreeNodes = [this.environmentService.userCostcenterTree];
                                //this.environmentService.userCostcenterTreeNodes.push(this.environmentService.userCostcenterTree);
                            },
                            (err: IError) => {
                                this.utilityHelperService.handleComponentError(err);
                            },
                        );
                    } else if (loginResult.application !== 'MWS-SETUP' && this.env.applicationName !== 'OCMWSI') {
                        this.appService.showToast(
                            'error',
                            this.coreService.getMsgkey('prompt.error', []),
                            this.coreService.getMsgkey('prompt.error.configuration.nousercostcenter', []),
                        );
                    }
                    if (loginResult.loginname.toLowerCase() !== 'orgacard') {
                        const user = results[3] as IUserBasedata;
                        const country = this.environmentService.countryList.find((c) => c.id === user.countryId);
                        if (country && country.countryIso3166) {
                            this.environmentService.appSettings.locale = loginResult.language + '_' + country.countryIso3166.toUpperCase();
                        } else {
                            this.environmentService.appSettings.locale = loginResult.language + '_' + loginResult.language.toUpperCase();
                        }
                    }
                    // this.environmentService.countryList.forEach(c => {
                    //     c.countrylanguages.forEach(l => {
                    //         // console.log('systemlogin.component.ts: VARIANT: ' + l.language.languageIso639 + '_' + c.countryIso3166);
                    //         const variant: IVariant = {
                    //             variant: l.language.languageIso639 + '_' + c.countryIso3166,
                    //             //description: l.language.descr + ' - ' + c.descr,
                    //             description: c.descr,
                    //             styleClassCountry: c.styleClass,
                    //             styleClassLanguage: l.language.styleClass
                    //         }
                    //         this.environmentService.variantList.push(variant);
                    //         if (this.environmentService.variantHash.get(l.language.languageIso639)) {
                    //             this.environmentService.variantHash.get(l.language.languageIso639).push(c);
                    //         } else {
                    //             let countries = [c];
                    //             this.environmentService.variantHash.set(l.language.languageIso639, countries);
                    //         }
                    //     })
                    // });
                    //
                    if (this.environmentService.mandator && this.environmentService.mandator.currencyId) {
                        this.coreService.getCurrencyById(this.environmentService.mandator.currencyId).subscribe(
                            (currency) => {
                                this.environmentService.mandatorCurrency = currency;
                            },
                            (err: IError) => {
                                this.utilityHelperService.handleComponentError(err);
                            },
                        );
                    }
                    // get SystemUserGender
                    if (this.environmentService.systemUser && this.environmentService.systemUser.genderId) {
                        this.genderService.getGenderById(this.environmentService.systemUser.genderId).subscribe(
                            (gender) => {
                                this.environmentService.systemUserGender = gender;
                            },
                            (err: IError) => {
                                this.utilityHelperService.handleComponentError(err);
                            },
                        );
                    }
                    //
                    this.router.navigate(['/mwsp/home']);
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                    this.logout();
                },
            );
        }
    }

    public logout() {
        localStorage.removeItem('loggedInUser');
        this.router.navigate(['/']);
    }

    public init() {
        this.environmentService.init();
        this.supplierService.init();
        this.genderService.init();
    }

    public defineAbility() {
        // WICHITIG: https://github.com/stalniy/casl/issues/305
        // CASL v4
        // const {can, rules} = new AbilityBuilder<AppAbility>();

        // CASL v5
        const { can, rules } = new AbilityBuilder(AppAbility);

        // can('execute', 'supplier');
        // can('read', 'supplierBasedata');
        // can(['read','update'], 'supplierBasedata'); // AUCH OK
        // can('update', 'supplierBasedata');          // AUCH OK
        if (this.environmentService.systemUser.privileges && this.environmentService.systemUser.privileges.length > 0) {
            this.environmentService.systemUser.privileges.forEach((p) => {
                // console.log('- ' + p);

                // VARIANTE [c,r,u,d]
                // +2 wg. ~[

                // CASL v4
                // let subject: any = p.substring(0, p.lastIndexOf('~'));
                // let actions: string[] = (p.substring(p.lastIndexOf('~') + 2, p.length-1)).split(',');
                // actions.forEach( a => {
                //     switch (a) {
                //         case 'c':
                //             can('create', subject);
                //             break;
                //         case 'r':
                //             can('read', subject);
                //             break;
                //         case 'u':
                //             can('update', subject);
                //             break;
                //         case 'd':
                //             can('delete', subject);
                //             break;
                //         case 'x':
                //             can('execute', subject);
                //             break;
                //     }
                // })

                // CASL v5
                const field: any = p.substring(0, p.lastIndexOf('~'));
                const actions: string[] = p.substring(p.lastIndexOf('~') + 2, p.length - 1).split(',');
                can(actions as appActions[], 'MWS', field);

                // VARARIANT: create, read, update, delete explizit
                // let subject: any = p.substring(0, p.lastIndexOf('.'));
                // let action: any = p.substring(p.lastIndexOf('.') + 1);
                // if (action === '*') {
                //     action = 'manage';
                // }
                // can(action, subject);

                //
                //
                // 'all' funktioniert bei 'nestedFields' oder complexTypes nicht !!!
                // siehe: https://github.com/stalniy/casl/issues/194
                // siehe: https://github.com/stalniy/casl/issues/143
                // if (subject.substring( subject.lastIndexOf('.') + 1 ) === '*') {
                //     subject = subject.substring( 0, subject.lastIndexOf('.') + 1 ) + 'all';
                // }
                //
                //
                // console.log('can (action, subject)');
                // console.log('can (' + action + ', ' + subject + ')');
            });
        }

        this.ability.update(rules);

        // console.log( ' ++++++++++++++++++++++++++++++++++++++++++++++++++ ' );
        // rules.forEach( r => {
        //     console.log( 'RULE: ' + r['action'] + ' ---> ' + r.subject);
        // })
        // console.log(' ++++++++++++++++++++++++++++++++++++++++++++++++++ ');
        // console.log("EXEC   - supplier: ", this.ability.can('execute', 'supplier'));
        // console.log("READ   - supplierBasedata: ", this.ability.can('read', 'supplierBasedata'));
        // console.log("EXEC   - supplierBasedata: ", this.ability.can('execute', 'supplierBasedata'));
        // console.log("READ   - supplierBasedata.name: ", this.ability.can('read', 'supplierBasedata.name'));
        // console.log("UPDATE - supplierBasedata.name: ", this.ability.can('update', 'supplierBasedata.name'));
        // console.log("CREATE - supplierBasedata.name: ", this.ability.can('create', 'supplierBasedata.name'));
        // console.log("READ - supplierContracts: ", this.ability.can('read', 'supplierContracts'));
        // console.log("READ - supplierContractDetail: ", this.ability.can('read', 'supplierContractDetail'));
        // console.log("READ - supplierContractCostcenterCustomerDetails: ", this.ability.can('read', 'supplierContractCostcenterCustomerDetails'));
    }

    public doShowMessage(msg: IOCMessage) {
        if (this.environmentService.appSettings.showLog) console.log('-----> *** showMessage: ' + msg.text + ' ***');
        //
        if (msg.headline.indexOf('OC-000009') >= 0) {
            this.appService.showToast(msg.level, msg.headline, this.coreService.getMsgkey('error.cache_storage'));
        } else {
            this.appService.showToast(msg.level, msg.headline, msg.text);
        }
    }

    public doLoading(loading: boolean) {
        if (loading) {
            if (this.environmentService.appSettings.showLog) console.log('----> START LOADING');
            this.loadingOverlayService.startLoading();
        } else {
            if (this.environmentService.appSettings.showLog) console.log('----> STOP LOADING');
            this.loadingOverlayService.stopLoading();
        }
    }

    public loadMsgtexts() {
        // TODO: Umstellen auf FORK_JOIN !!!
        this.coreService.getMsgtexts().subscribe((data: any) => {
            if (this.environmentService.appSettings.showLog) console.log(new Date() + ': ' + 'login.component.ts - getMsgtexts ---> RESPONSE data: ' + data);
        });
    }
}
